<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { STEP_TYPES } from './treeOps';

const props = defineProps({
  insertArmed: { type: Boolean, default: false },
  lifecycleEnabled: { type: Boolean, default: true },
});
const emit = defineEmits(['pick']);
const { t } = useI18n();
const visibleStepTypes = computed(() =>
  props.lifecycleEnabled
    ? STEP_TYPES
    : STEP_TYPES.filter(type => type !== 'UpdateLifecycleStage')
);

const ICONS = {
  SendMessage: '💬',
  QuestionStep: '❓',
  BranchStep: '🔀',
  Jump: '🔁',
  AssignTo: '👤',
  WaitStep: '⏱️',
  CloseConversation: '✅',
  CsatStep: '⭐',
  SendAttachment: '📎',
  DateTime: '📅',
  UpdateContactField: '✏️',
  SetVariable: '🧩',
  UpdateContactTags: '🏷️',
  UpdateLifecycleStage: '🧭',
  AddComment: '📝',
  OpenConversation: '🔓',
  TriggerByAnotherFlow: '↪️',
  HTTPRequest: '🌐',
  WhatsAppList: '📋',
  AIStep: '✨',
};

// The help panel stays available even while inserting is not armed (the step
// button itself is disabled then), so a user can always read what a step does
// before arming an insert point.
const HELP_MARK = '!';
const openHelp = ref('');
const search = ref('');
const advancedOpen = ref(false);
const toggleHelp = type => {
  openHelp.value = openHelp.value === type ? '' : type;
};

const GROUPS = [
  {
    key: 'core',
    types: ['SendMessage', 'QuestionStep', 'WaitStep', 'CloseConversation'],
  },
  {
    key: 'logic',
    types: ['BranchStep', 'Jump', 'SetVariable', 'TriggerByAnotherFlow'],
  },
  {
    key: 'conversation',
    types: [
      'AssignTo',
      'AddComment',
      'UpdateContactTags',
      'UpdateContactField',
      'UpdateLifecycleStage',
      'OpenConversation',
    ],
  },
  {
    key: 'integrations',
    types: ['WhatsAppList', 'SendAttachment', 'DateTime'],
  },
];
const ADVANCED_TYPES = ['CsatStep', 'HTTPRequest', 'AIStep'];
const normalizedSearch = computed(() => search.value.trim().toLowerCase());
const matches = type => {
  if (!visibleStepTypes.value.includes(type)) return false;
  if (!normalizedSearch.value) return true;
  return [type, t(`TELETEL_FLOWS.BUILDER.STEP.${type}`)]
    .join(' ')
    .toLowerCase()
    .includes(normalizedSearch.value);
};
const visibleGroups = computed(() =>
  GROUPS.map(group => ({
    ...group,
    types: group.types.filter(matches),
  })).filter(group => group.types.length)
);
const visibleAdvanced = computed(() => ADVANCED_TYPES.filter(matches));
const expandedAdvanced = computed(() =>
  advancedOpen.value || normalizedSearch.value ? visibleAdvanced.value : []
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <input
      v-model="search"
      type="search"
      class="mb-0 text-sm"
      :placeholder="t('TELETEL_FLOWS.BUILDER.SEARCH_STEPS')"
    />
    <section
      v-for="group in visibleGroups"
      :key="group.key"
      class="flex flex-col gap-1.5"
    >
      <h3 class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
        {{ t(`TELETEL_FLOWS.BUILDER.STEP_GROUPS.${group.key}`) }}
      </h3>
      <div v-for="type in group.types" :key="type" class="flex flex-col gap-1">
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="flex items-center flex-1 gap-2 px-2 py-1.5 text-sm rounded-lg border text-start"
            :class="
              insertArmed
                ? 'border-n-brand text-n-slate-12 hover:bg-n-brand/10 cursor-pointer'
                : 'border-n-weak text-n-slate-10 cursor-not-allowed opacity-60'
            "
            :disabled="!insertArmed"
            @click="emit('pick', type)"
          >
            <span aria-hidden="true">{{ ICONS[type] }}</span>
            <span class="flex-1">{{
              t(`TELETEL_FLOWS.BUILDER.STEP.${type}`)
            }}</span>
          </button>
          <button
            type="button"
            class="flex items-center justify-center flex-shrink-0 text-xs font-bold border rounded-full size-6 border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
            :class="openHelp === type ? 'bg-n-alpha-2 text-n-brand' : ''"
            :aria-expanded="openHelp === type"
            :aria-label="t('TELETEL_FLOWS.BUILDER.STEP_HELP_LABEL')"
            :title="t(`TELETEL_FLOWS.BUILDER.STEP_HELP.${type}`)"
            @click="toggleHelp(type)"
          >
            {{ HELP_MARK }}
          </button>
        </div>
        <p
          v-if="openHelp === type"
          class="px-2 py-1.5 text-xs leading-5 rounded-lg bg-n-alpha-1 text-n-slate-11"
        >
          {{ t(`TELETEL_FLOWS.BUILDER.STEP_HELP.${type}`) }}
        </p>
      </div>
    </section>
    <section v-if="visibleAdvanced.length" class="flex flex-col gap-1.5">
      <button
        type="button"
        class="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-n-slate-10"
        :aria-expanded="advancedOpen"
        @click="advancedOpen = !advancedOpen"
      >
        {{ t('TELETEL_FLOWS.BUILDER.ADVANCED_STEPS') }}
        <span aria-hidden="true">{{ advancedOpen ? '−' : '+' }}</span>
      </button>
      <div
        v-for="type in expandedAdvanced"
        :key="type"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="flex items-center flex-1 gap-2 px-2 py-1.5 text-sm rounded-lg border text-start"
            :class="
              insertArmed
                ? 'border-n-brand text-n-slate-12 hover:bg-n-brand/10 cursor-pointer'
                : 'border-n-weak text-n-slate-10 cursor-not-allowed opacity-60'
            "
            :disabled="!insertArmed"
            @click="emit('pick', type)"
          >
            <span aria-hidden="true">{{ ICONS[type] }}</span>
            {{ t(`TELETEL_FLOWS.BUILDER.STEP.${type}`) }}
          </button>
          <button
            type="button"
            class="flex items-center justify-center flex-shrink-0 text-xs font-bold border rounded-full size-6 border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
            :class="openHelp === type ? 'bg-n-alpha-2 text-n-brand' : ''"
            :aria-expanded="openHelp === type"
            :aria-label="t('TELETEL_FLOWS.BUILDER.STEP_HELP_LABEL')"
            :title="t(`TELETEL_FLOWS.BUILDER.STEP_HELP.${type}`)"
            @click="toggleHelp(type)"
          >
            {{ HELP_MARK }}
          </button>
        </div>
        <p
          v-if="openHelp === type"
          class="px-2 py-1.5 text-xs leading-5 rounded-lg bg-n-alpha-1 text-n-slate-11"
        >
          {{ t(`TELETEL_FLOWS.BUILDER.STEP_HELP.${type}`) }}
        </p>
      </div>
    </section>
    <p
      v-if="!visibleGroups.length && !visibleAdvanced.length"
      class="text-xs text-n-slate-10"
    >
      {{ t('TELETEL_FLOWS.BUILDER.NO_STEP_RESULTS') }}
    </p>
    <p v-if="!insertArmed" class="mt-2 text-xs text-n-slate-10">
      {{ `${t('TELETEL_FLOWS.BUILDER.ADD_HERE')} ⊕` }}
    </p>
  </div>
</template>
